import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Expand } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Reveal } from "./Reveal";
import { LavoriRealizzati } from "./LavoriRealizzati";

const PROJECTS = [
    {
        title: "Portale di lavaggio Christ ALPHA",
        source: "Christ Wash Systems",
        category: "Autolavaggi",
        img: "/christ/alpha.jpg",
    },
    {
        title: "Tunnel di lavaggio Christ EVO STAR",
        source: "Christ Wash Systems",
        category: "Autolavaggi",
        img: "/christ/evo-star.jpg",
    },
    {
        title: "Pista self-service Christ BLADE",
        source: "Christ Wash Systems",
        category: "Autolavaggi",
        img: "/christ/self-service.jpg",
    },
    {
        title: "Lavaggio mezzi pesanti Christ NOVA",
        source: "Christ Wash Systems",
        category: "Autolavaggi",
        img: "/christ/camion.jpg",
    },
    {
        title: "Officina attrezzata con sollevatori Ravaglioli",
        source: "Ravaglioli",
        category: "Officine",
        img: "/officina/officina-ravaglioli.jpg",
    },
    {
        title: "Arredo e utensileria professionale per officina",
        source: "Beta Utensili",
        category: "Officine",
        img: "/brands/beta-arredo.jpg",
    },
    {
        title: "Impianto a osmosi inversa per il trattamento acque",
        source: "Idroconsult",
        category: "Acque",
        img: "/brands/idroconsult-osmosi.jpg",
    },
    {
        title: "Impianto di aspirazione gas di scarico",
        source: "Filcar",
        category: "Industriali",
        img: "/brands/filcar-aspirazione.jpg",
    },
    {
        title: "Compressori e impianti aria compressa",
        source: "ABAC",
        category: "Industriali",
        img: "/brands/abac-compressori.jpg",
    },
];

const FILTERS = ["Tutti", "Autolavaggi", "Officine", "Acque", "Industriali"];

export const Gallery = () => {
    const [filter, setFilter] = useState("Tutti");
    const [selected, setSelected] = useState(null);

    const visible =
        filter === "Tutti" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

    return (
        <section
            id="realizzazioni"
            data-testid="gallery-section"
            className="bg-white py-20 lg:py-28"
        >
            <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
                <LavoriRealizzati />
                <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
                    <div className="max-w-xl">
                        <Reveal>
                            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                                Realizzazioni
                            </span>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl">
                                Gli impianti che installiamo nel Sud Italia.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <p className="mt-4 text-sm leading-relaxed text-[#4F5D70] sm:text-base">
                                Foto ufficiali dei costruttori partner. Gli impianti FTM installati sul
                                territorio sono visitabili su richiesta.
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
                            {FILTERS.map((f) => (
                                <button
                                    key={f}
                                    data-testid={`gallery-filter-${f.toLowerCase()}`}
                                    onClick={() => setFilter(f)}
                                    className={`rounded-full px-4 py-2 text-xs font-bold transition-[background-color,color] duration-300 ${
                                        filter === f
                                            ? "bg-[#0B1F3A] text-white"
                                            : "bg-slate-100 text-[#475569] hover:bg-slate-200"
                                    }`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                </div>

                <motion.div layout className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                        {visible.map((p, i) => (
                            <motion.article
                                layout
                                key={p.title}
                                initial={{ opacity: 0, scale: 0.94 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.94 }}
                                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                                data-testid={`gallery-item-${p.category.toLowerCase()}-${i}`}
                                onClick={() => setSelected(p)}
                                className="group relative h-72 cursor-pointer overflow-hidden rounded-3xl"
                            >
                                <img
                                    src={p.img}
                                    alt={p.title}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-108"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/85 via-[#0B1F3A]/15 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
                                <div className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 opacity-0 backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
                                    <Expand className="h-4 w-4 text-white" />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <span className="rounded-full bg-[#E30613] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                                        {p.category}
                                    </span>
                                    <h3 className="font-display mt-3 text-lg font-bold text-white">
                                        {p.title}
                                    </h3>
                                    <div className="mt-1 flex items-center gap-1.5 text-xs text-white/70">
                                        <Camera className="h-3.5 w-3.5" />
                                        Foto: {p.source}
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
                <DialogContent
                    className="max-w-3xl overflow-hidden border-0 bg-transparent p-0 shadow-none"
                    data-testid="gallery-modal"
                >
                    <DialogTitle className="sr-only">
                        {selected ? selected.title : "Dettaglio realizzazione"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Immagine ingrandita della realizzazione selezionata
                    </DialogDescription>
                    {selected && (
                        <div>
                            <img
                                src={selected.img}
                                alt={selected.title}
                                className="max-h-[70vh] w-full rounded-2xl object-cover"
                            />
                            <div className="mt-4 rounded-2xl bg-white p-5">
                                <span className="rounded-full bg-[#E30613] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                                    {selected.category}
                                </span>
                                <h3 className="font-display mt-2 text-xl font-bold text-[#0B1F3A]">
                                    {selected.title}
                                </h3>
                                <div className="mt-1 flex items-center gap-1.5 text-sm text-[#4F5D70]">
                                    <Camera className="h-4 w-4" />
                                    Foto ufficiale {selected.source} · installazione e assistenza FTM Group nel Sud Italia
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
};
